import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, Space, Modal, message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// eslint-disable-next-line max-len
import { type LocalCustomerManagementModuleSpecsFragment } from '../../../../api/fragments/LocalCustomerManagementModuleSpecs';
import {
    DeleteKycPresetsDocument,
    type DeleteKycPresetsMutation,
    type DeleteKycPresetsMutationVariables,
} from '../../../../api/mutations/deleteKYCPresets';

export type KYCPresetActionsProps = {
    module: LocalCustomerManagementModuleSpecsFragment;
    kycPreset: LocalCustomerManagementModuleSpecsFragment['kycPresets'][number];
};

const KYCPresetActions = ({ module, kycPreset }: KYCPresetActionsProps) => {
    const moduleId = module.id;
    const kycPresetId = kycPreset.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['localCustomerModuleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('localCustomerModuleDetails:kycPresets.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('localCustomerModuleDetails:kycPresets.deleteModal.content'),
            okText: t('localCustomerModuleDetails:kycPresets.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('localCustomerModuleDetails:kycPresets.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('localCustomerModuleDetails:kycPresets.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<DeleteKycPresetsMutation, DeleteKycPresetsMutationVariables>({
                        mutation: DeleteKycPresetsDocument,
                        variables: { moduleId, kycPresetId },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('localCustomerModuleDetails:kycPresets.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [apolloClient, moduleId, kycPresetId, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            <Button
                onClick={() => navigate(`/admin/system/modules/${moduleId}/kyc/${kycPresetId}`)}
                size="small"
                style={{ padding: 0 }}
                type="link"
            >
                {t('localCustomerModuleDetails:kycPresetsTable.actions.update')}
            </Button>
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('localCustomerModuleDetails:kycPresetsTable.actions.delete')}
            </Button>
        </Space>
    );
};

export default KYCPresetActions;
